// [S-073a] Upload mutation — POSTs a single file to the admin
// /tenants/:id/documents/upload route landed in S-045 (apps/admin/src/
// routes/documents.ts). The bulk-upload UX in Upload.tsx loops over a
// staged file list and fires this mutation per file; the admin route
// accepts one file per request.
//
// Infer-first: classification (deal type / guild / tier) is auto-detected by
// Stage-2 extraction and corrected in the review queue — there is no
// upload-time tagging, so this mutation sends the file only.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  ApiError,
  getApiErrorMessage,
  isBackendUnreachableError,
  useApi,
} from "./api";

// Mirrors DocumentLifecycleState in packages/core/src/shared-kernel.
// Duplicated as a string-literal union here to avoid pulling
// @csai/core into the web bundle just for an enum (the pattern other
// data hooks like reAttestationQuery already follow).
export type DocumentLifecycleState =
  | "received"
  | "parsing"
  | "stage1_complete"
  | "stage2_review"
  | "stage3_human"
  | "indexed"
  | "failed";

export const TERMINAL_LIFECYCLE_STATES: ReadonlySet<DocumentLifecycleState> =
  new Set<DocumentLifecycleState>(["indexed", "failed"]);

export function isTerminalLifecycle(
  state: DocumentLifecycleState | undefined
): boolean {
  return state !== undefined && TERMINAL_LIFECYCLE_STATES.has(state);
}

export type SingleUploadResult = {
  zip?: undefined;
  documentId: string;
  lifecycleState: DocumentLifecycleState;
  gcsUri: string;
  duplicate: boolean;
};

// [S-267] One entry's outcome inside a .zip archive. `error` is set when that
// specific file failed to ingest; `documentId`/`lifecycleState` otherwise.
export type ZipEntryOutcome = {
  filename: string;
  documentId?: string;
  lifecycleState?: DocumentLifecycleState;
  duplicate?: boolean;
  error?: string;
};

export type ZipSkippedEntry = { filename: string; reason: string };

// [S-267] The server's response when a .zip was uploaded: a per-file summary
// rather than a single document. Skipped entries (unsupported type, unsafe
// path, nested archive) are reported so the UI never silently drops a file.
export type ZipUploadResult = {
  zip: true;
  totalEntries: number;
  ingested: number;
  duplicates: number;
  skipped: ZipSkippedEntry[];
  files: ZipEntryOutcome[];
};

export type UploadFileResult = SingleUploadResult | ZipUploadResult;

export type UploadFileInput = {
  tenantId: string;
  file: File;
  // [S-268] Lets the caller cancel an in-flight upload (per-row ✕ / cancel-all).
  signal?: AbortSignal;
};

export function useUploadFile(): UseMutationResult<
  UploadFileResult,
  Error,
  UploadFileInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, file, signal }) => {
      const body = new FormData();
      body.append("file", file);
      return await api<UploadFileResult>(
        `/tenants/${encodeURIComponent(tenantId)}/documents/upload`,
        {
          method: "POST",
          body,
          ...(signal !== undefined ? { signal } : {}),
        }
      );
    },
    onSuccess: () => {
      // Surfacing the new doc in the activity feed is a server-driven
      // refresh (audit middleware emits the event); invalidating the
      // activity query keeps the Home dashboard's "Recent activity"
      // and the full /activity feed honest after a successful upload.
      void qc.invalidateQueries({ queryKey: ["tenant", "activity"] });
    },
  });
}

// [S-268] A cancelled upload rejects with an AbortError (DOMException) — not an
// Error subclass in every runtime — so match on the name defensively.
export function isAbortError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name?: unknown }).name === "AbortError"
  );
}

// Helper for the screen — surfaces a typed error message vs. a generic
// Error. ApiError exposes `status` + `body` so we can show "duplicate"
// (200 with `duplicate: true`) differently from a 400/500.
export function describeUploadError(err: unknown): string {
  // [S-268] Cancel + network failures get plain-language copy instead of a raw
  // "Failed to fetch" (a browser fetch rejection isn't an ApiError).
  if (isAbortError(err)) return "Canceled.";
  // [dev-loop bugfix] A request that never got an answer — fetch rejecting
  // (offline) OR the proxy reporting it couldn't reach the API — is now one
  // typed case (ApiError, status 0 or a body-less 5xx) instead of a raw
  // TypeError. Checked BEFORE the status ladder below, which would otherwise
  // read status 0 as "Upload failed: 0" and a proxy 500 as a server error the
  // server never produced. This screen keeps its own connection-oriented copy
  // (it has a Retry button right there) rather than api.ts's dev-stack hint.
  if (isBackendUnreachableError(err)) {
    return "Network error — check your connection and retry.";
  }
  if (err instanceof ApiError) {
    if (err.status === 400) return "File rejected by server (bad format?)";
    if (err.status === 401 || err.status === 403)
      return "Not authorized to upload to this tenant.";
    // [S-267] 413 = a ZIP guardrail tripped (too big, too many files, or a
    // zip-bomb guard). The server sends a specific human-readable message.
    if (err.status === 413) {
      return (
        getApiErrorMessage(err) ?? "Archive too large or has too many files."
      );
    }
    if (err.status >= 500) return "Server error — try again in a moment.";
    return `Upload failed: ${err.status}`;
  }
  // A fetch that never reached the server (offline, DNS, CORS) throws a
  // TypeError "Failed to fetch" — translate it to something actionable.
  if (
    err instanceof TypeError ||
    (err instanceof Error && /failed to fetch|network/i.test(err.message))
  ) {
    return "Network error — check your connection and retry.";
  }
  if (err instanceof Error) return err.message;
  return "Upload failed";
}

// Lifecycle-status polling for a single document. The admin server
// returns the current `lifecycleState` from
// `GET /tenants/:id/documents/:docId` (S-045). We poll every 2s while
// the document is in flight, and stop once it reaches a terminal state
// — `indexed` (success, ready to surface a CIR deep link) or `failed`
// (recovery is platform_admin-only via S-073b; we never expose retry
// to the tenant_user audience).
export type DocumentClassification = {
  dealType?: string;
  guild?: string;
  talentTier?: string;
  visibility?: string;
};

export type DocumentStatusResponse = {
  documentId: string;
  tenantId: string;
  lifecycleState: DocumentLifecycleState;
  documentHash: string;
  uploadedAt: string;
  uploadedBy: string;
  // S-216 — effective LLM classification (+ uploader overrides) for per-file
  // display on the upload portal. Absent on older docs / pre-classification.
  classification?: DocumentClassification;
  // [failure-visibility] Why this document is `failed`. Null for every other
  // state, and for documents that failed before the reason was recorded (it
  // only ever existed in the ingestion container's log, which the app can't
  // read back). Lets the row explain itself instead of showing a bare pill.
  failure?: {
    /** "stage1" | "stage2" | "stage3" | "hybrid". */
    stage: string;
    message: string;
    at: string;
  } | null;
};

export const DOCUMENT_STATUS_POLL_MS = 2000;

export function documentStatusKey(
  tenantId: string | null,
  documentId: string | null
): readonly unknown[] {
  return ["tenant", tenantId, "document", documentId, "status"] as const;
}

// [S-264] Once an uploaded doc reaches the human review queue there's nothing
// more for the *uploader* to watch — a reviewer advances it from there. Since
// upload rows now persist (they no longer auto-remove), we must stop polling at
// the review states or a doc sitting in the queue would poll every 2s forever.
const UPLOAD_SETTLED_STATES: ReadonlySet<DocumentLifecycleState> = new Set([
  "indexed",
  "failed",
  "stage2_review",
  "stage3_human",
]);

export function useDocumentStatus(
  tenantId: string | null,
  documentId: string | null,
  opts: { stopAtReview?: boolean } = {}
): UseQueryResult<DocumentStatusResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: documentStatusKey(tenantId, documentId),
    enabled: tenantId !== null && documentId !== null,
    queryFn: () =>
      api<DocumentStatusResponse>(
        `/tenants/${encodeURIComponent(tenantId!)}/documents/${encodeURIComponent(documentId!)}`
      ),
    // Stop polling once the document hits a terminal state (or, when
    // stopAtReview is set, once it reaches the review queue). TanStack accepts
    // a function returning false to halt; otherwise we tick every
    // DOCUMENT_STATUS_POLL_MS ms.
    refetchInterval: (query) => {
      const state = query.state.data?.lifecycleState;
      const stop =
        opts.stopAtReview === true
          ? state !== undefined && UPLOAD_SETTLED_STATES.has(state)
          : isTerminalLifecycle(state);
      return stop ? false : DOCUMENT_STATUS_POLL_MS;
    },
    // Browser focus is irrelevant here — the lifecycle moves on the
    // server's clock.
    refetchOnWindowFocus: false,
  });
}

// CIR deep link into the precedents panel for an indexed document.
// Format: `contractstudio://collection/{tenant}/{docId}` — matches the
// link convention called out in the S-073a spec (test gate: "CIR deep
// link from `indexed` status → navigates to the precedents panel
// scoped to that document"). The web app intercepts the
// `contractstudio:` scheme via the route registered in App.tsx — but
// for v1 we surface it as a plain anchor that points at the in-app
// /precedent route filtered to the document. The custom scheme is for
// the future native shell; the web fallback is what tenant_users see
// today.
export function makeCirDeepLink(
  tenantId: string,
  documentId: string
): string {
  return `contractstudio://collection/${encodeURIComponent(tenantId)}/${encodeURIComponent(documentId)}`;
}

// Web-app fallback for the CIR deep link — opens the precedents page
// with a documentId filter so the user lands on a panel scoped to the
// document they just uploaded. Used by the in-app "Open in precedents"
// affordance until the native shell handles `contractstudio://`.
export function makePrecedentRouteFor(documentId: string): string {
  return `/precedent?documentId=${encodeURIComponent(documentId)}`;
}
